import React, { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import "reflect-metadata";
import "./index.scss";
import * as serviceWorker from "./serviceWorker";
import { ConnectedRouter } from "connected-react-router/immutable";
import configureStore from "./store/configureStore";
import "./groupApp";
import { Provider } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TagManager from "react-gtm-module";
import "react-csv-importer/dist/index.css";
import {
  QueryClientProvider,
  QueryClient,
} from 'react-query';
import { HelmetProvider } from 'react-helmet-async';
import { clarity } from 'react-microsoft-clarity';
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import miscUtils from "./utils/miscUtils";
import Cookie from "js-cookie";
import RootErrorBoundary from "./components/RootErrorBoundary";
import RootFallback from "./components/RootFallback";
import { isChunkLoadError, isDevServer, recoverFromChunkError, retryImport } from "./utils/chunkRecovery";

// Lazy load the components
const LivePageContainer = lazy(() => retryImport(() => import("./containers/PageBuilder/LivePageContainer")));
const MainComponent = lazy(() => retryImport(() => import("./containers/main")));
// Banners stay on a plain import(): a missing banner chunk must not reload the page.
const SmartAppBannerComp = lazy(() => import("./components/SmartAppBanner/SmartAppBanner"));
const UpgradePlanBannerComp = lazy(() => import("./components/UpgradePlanBanner/UpgradePlanBanner"));
const ClientThumbmarkProvider = lazy(() => retryImport(() => import("./components/ClientThumbmarkProvider")));

// Vite HMR re-runs this entry module when an edit reaches it; a second run reloads the page instead of mounting twice.
const isHmrRerun = typeof window !== 'undefined' && (window as any).__groupAppBooted === true;
if (typeof window !== 'undefined') {
  (window as any).__groupAppBooted = true;
}

if(typeof window !== 'undefined') {
  // Covers the raw import() calls scattered through the app, which reject with
  // nothing to catch them.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason;
    if (!isChunkLoadError(reason) || isDevServer) return;
    // This handler owns chunk recovery, so the rejection must not also surface as an uncaught error.
    event.preventDefault();
    const message = (reason && reason.message) || 'unhandled chunk rejection';
    recoverFromChunkError(message).then((willReload) => {
      if (!willReload) {
        console.error('Chunk load failed and this build already used its reload', reason);
      }
    });
  });

  // Vite fires this when a chunk's JS or CSS fails to load; the page reloads before a retry can render without CSS.
  window.addEventListener('vite:preloadError', (event: any) => {
    const payload = event && event.payload;
    recoverFromChunkError((payload && payload.message) || 'chunk preload failed');
  });

  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  const _fbc = Cookie.get('_fbc');

  if(fbclid && !_fbc) {
    const version = 'fb';
    const subdomainIndex = miscUtils.getSubdomainIndex(window.location.hostname);
    const creationTime = Date.now();
    const formattedValue = `${version}.${subdomainIndex}.${creationTime}.${fbclid}`;

    Cookie.set('_fbc', formattedValue, { path: "/", domain: window.location.hostname, expires: 90 });
  }
}

// Analytics SDKs require browser globals and must not run while the SSR server
// imports this module to render the React tree.
if (
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  process.env.REACT_APP_ENV === "production"
) {
  const tagManagerArgs = {
    gtmId: "GTM-5LP5HHB"
  };
  TagManager.initialize(tagManagerArgs);
}

if (
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  ((process.env.REACT_APP_ENV === "production") || (process.env.REACT_APP_ENV === "staging"))
) {
  Sentry.init({
    dsn: 'https://417f82af114e4ea592b37c4186de7e36@o266270.ingest.us.sentry.io/1547076',
    environment: process.env.REACT_APP_ENV,
    // @ts-ignore
    integrations: [new BrowserTracing()],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production.
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    // Errors from code that in-app browsers (Facebook, Instagram) inject, and a harmless browser warning.
    ignoreErrors: [
      "_AutofillCallbackHandler",
      "Java object is gone",
      "ResizeObserver loop",
    ],
  });
}

if(typeof window !== 'undefined') {
  if (process.env.REACT_APP_ENV === "production") {
    clarity.init('m4v1fa7c35');
  }
}

// Authentication pages must never show the community's brand-tinted canvas.
// Tag <body> with `auth-page` as early as possible — before React renders and
// before ThemeTokenApplier sets --p-canvas — so the white background is in
// place on first paint and there is no tint flash on reload. AuthenticationLayout
// keeps this class in sync during client-side route changes.
if (typeof window !== 'undefined' && typeof document !== 'undefined' && document.body) {
  const AUTH_PATHS = [
    '/signin',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/new-password',
    '/new-name'
  ];
  const path = window.location.pathname;
  if (AUTH_PATHS.some(p => path === p || path.indexOf(p + '/') === 0)) {
    document.body.classList.add('auth-page');
  }
  // Checkout / purchase pages also stay white regardless of community theme.
  if (
    path.indexOf('plan-subscription') !== -1 ||
    path.indexOf('/checkout') !== -1 ||
    path.indexOf('/purchase') !== -1
  ) {
    document.body.classList.add('checkout-page');
  }
  // Lecture / lesson pages keep a white top bar — both /courses/:id/lectures/...
  // and /library/:type/lectures/... have "lectures" as the 4th path segment.
  if (path.split('/')[3] === 'lectures') {
    document.body.classList.add('lecture-page');
  }
  // Newsletter pages (/newsletter and /newsletter/:id) stay white regardless of
  // theme — tag early so there is no brand-tint flash before React renders.
  if (path.split('/')[1] === 'newsletter') {
    document.body.classList.add('newsletter-page');
  }
}

toast.configure({
  hideProgressBar: true
});

// Initialize Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
    },
  },
});

// Add scripts conditionally
const addScripts = () => {
  if (typeof document === 'undefined') {
    return;
  }

  // Add your scripts here
  const script1 = document.createElement('script');
  script1.innerHTML = `
  (function(w) {
    w.fpr = w.fpr || function() {
      w.fpr.q = w.fpr.q || [];
      w.fpr.q[arguments[0] == "set" ? "unshift" : "push"](arguments);
    };
  })(window);
  fpr("init", { cid: "85fvobma" });
  fpr("click");
  `;
  document.head.appendChild(script1);

  const script2 = document.createElement('script');
  script2.innerHTML = `
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
    t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", "m4v1fa7c35");
  `;
  document.head.appendChild(script2);

  const script3 = document.createElement('script');
  script3.async = true;
  script3.src = `https://cdn.tolt.io/tolt.js`;
  script3.setAttribute(`data-tolt`, `d02be954-ef33-49a5-b0b5-94ba374a8cb0`);
  document.head.appendChild(script3);

  const script4 = document.createElement('script');
  script4.async = true;
  script4.src = `https://js.stripe.com/v3/`;
  document.body.appendChild(script4);

  if (process.env.REACT_APP_ENV === "production") {
    const script5 = document.createElement('script');
    script5.innerHTML = `
    !function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");
    oaiq("init",{pixelId:"W17e5syQ42TKw1UGvPNKLq"});
    `;
    document.head.appendChild(script5);
  }
};

const Root = () => {

  if (typeof window !== 'undefined' && window.location.pathname.split("/")[1] === "pages") {
    return (
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
            <Provider store={configureStore.store}>
              <ConnectedRouter history={configureStore.history}>
                <RootErrorBoundary>
                  <Suspense fallback={<RootFallback />}>
                    <ClientThumbmarkProvider
                    apiKey={process.env.REACT_APP_THUMBMARKJS_API_KEY}
                    >
                      <LivePageContainer />
                    </ClientThumbmarkProvider>
                  </Suspense>
                </RootErrorBoundary>
              </ConnectedRouter>
            </Provider>
        </QueryClientProvider>
      </HelmetProvider>
    )
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
          <Provider store={configureStore.store}>
            <ConnectedRouter history={configureStore.history}>
              <RootErrorBoundary>
                {/* Banners are optional: a failed banner chunk must not blank the app. */}
                <RootErrorBoundary silent={true}>
                  <Suspense fallback={null}>
                    <SmartAppBannerComp />
                    <UpgradePlanBannerComp />
                  </Suspense>
                </RootErrorBoundary>
                <Suspense fallback={<RootFallback />}>
                  <ClientThumbmarkProvider
                  apiKey={process.env.REACT_APP_THUMBMARKJS_API_KEY}
                  >
                    <MainComponent />
                  </ClientThumbmarkProvider>
                </Suspense>
              </RootErrorBoundary>
            </ConnectedRouter>
          </Provider>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default Root;

// Add scripts after the Root component is defined
if (!isHmrRerun) {
  addScripts();
}

// Mount the app in the browser into the empty #app the server sends
if (isHmrRerun) {
  (window as any).__groupAppReloading = true;
  window.location.reload();
} else if (typeof document !== 'undefined') {
  createRoot(document.getElementById("app") as HTMLElement).render(<Root />);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
